import type {
  IOnboardingOtherQuestionValue,
  IOnboardingQuestionValues,
  OnboardingQuestionValue,
} from '~/modules/onboarding/types';

import { ONBOARDING_STEPS } from '~/modules/onboarding/constants/onboarding-steps';
import { create } from 'zustand';

interface OnboardingStoreState {
  onboardingPath: string;
  isNavigatedToOnboardingFromApp: boolean;
  isNavigatedToSetupFromSurvey: boolean;
  currentStep: number;
  selectedQuestionValues: IOnboardingQuestionValues[];
  selectedOtherQuestionValue: IOnboardingOtherQuestionValue[];
}

interface OnboardingStoreActions {
  setOnboardingPath: (path: string) => void;
  setIsNavigatedToOnboardingFromApp: (isNavigatedToOnboardingFromApp: boolean) => void;
  setIsNavigatedToSetupFromSurvey: (isNavigatedToSetupFromSurvey: boolean) => void;
  nextStep: () => void;
  previousStep: () => void;
  setCurrentStep: (step: number) => void;
  setOnboardingQuestionValues: ({
    selectedQuestions,
    stepNumber,
  }: {
    selectedQuestions: OnboardingQuestionValue[];
    stepNumber: number;
  }) => void;
  setOnboardingOtherQuestionValues: ({
    newValue,
    stepNumber,
  }: {
    newValue: string | null;
    stepNumber: number;
  }) => void;
  reset: () => void;
}

const initialState: OnboardingStoreState = {
  onboardingPath: '',
  isNavigatedToOnboardingFromApp: false,
  isNavigatedToSetupFromSurvey: false,
  currentStep: 0,
  selectedQuestionValues: ONBOARDING_STEPS.map((step) => ({
    step: step.step,
    values: [],
  })),
  selectedOtherQuestionValue: ONBOARDING_STEPS.map((step) => ({
    step: step.step,
    value: null,
  })),
};

export const useOnboardingStore = create<OnboardingStoreState & OnboardingStoreActions>()(
  (set, get) => ({
    ...initialState,
    setOnboardingPath: (path: string) => set({ onboardingPath: path }),
    setIsNavigatedToOnboardingFromApp: (isNavigatedToOnboardingFromApp: boolean) =>
      set({ isNavigatedToOnboardingFromApp: isNavigatedToOnboardingFromApp }),
    setIsNavigatedToSetupFromSurvey: (isNavigatedToSetupFromSurvey: boolean) =>
      set({ isNavigatedToSetupFromSurvey: isNavigatedToSetupFromSurvey }),
    nextStep: () =>
      set(({ currentStep }) => ({
        currentStep: Math.min(currentStep + 1, get().selectedQuestionValues.length - 1),
      })),
    previousStep: () =>
      set(({ currentStep }) => ({
        currentStep: Math.max(currentStep - 1, 0),
      })),
    setCurrentStep: (step: number) => set({ currentStep: step }),
    setOnboardingQuestionValues: ({
      selectedQuestions,
      stepNumber,
    }: {
      selectedQuestions: OnboardingQuestionValue[];
      stepNumber: number;
    }) =>
      set(({ selectedQuestionValues }) => ({
        selectedQuestionValues: selectedQuestionValues.map((step) =>
          step.step === stepNumber ? { ...step, values: selectedQuestions } : step,
        ),
      })),
    setOnboardingOtherQuestionValues: ({
      newValue,
      stepNumber,
    }: {
      newValue: string | null;
      stepNumber: number;
    }) =>
      set(({ selectedOtherQuestionValue }) => ({
        selectedOtherQuestionValue: selectedOtherQuestionValue.map((step) =>
          step.step === stepNumber ? { ...step, value: newValue } : step,
        ),
      })),
    reset: () =>
      set((state) => ({
        ...initialState,
        isNavigatedToOnboardingFromApp: state.isNavigatedToOnboardingFromApp,
      })),
  }),
);
